<template>
  <div>
    <div
      v-if="draft && draft.pages"
      :class="{
        'board-navigation-overlay--expanded': expanded,
        'board-navigation-overlay--hidden': !visible,
        'board-navigation-overlay--fullscreen': editorIsFullscreen,
        'board-navigation-overlay--default': !editorIsFullscreen,
        'board-navigation-overlay--no-sections': !draftHasSections,
        'board-navigation-overlay--hide-nav-overlay': slideOverlayArrows && !editorIsFullscreen,
      }"
      class="board-navigation-overlay"
      v-on="activityTimeoutEnabled ? {
        mouseover: onMouseOver,
        mouseleave: onMouseLeave,
        keydown: onKeyDown,
      } : {}"
    >
      <div
        v-if="editorIsFullscreen"
        aria-live="polite"
        class="nebula-screenreader-only"
      >
        <div v-if="visible">
          {{ $t('Header visible') }}
        </div>
        <div v-else>
          {{ $t('Header hidden') }}
        </div>
      </div>
      <div class="board-navigation-overlay__toolbar">
        <!-- Toolbar for fullscreen display mode -->
        <template v-if="editorIsFullscreen">
          <div
            class="board-navigation-overlay__toolbar-group
              board-navigation-overlay__toolbar-group--start"
          >
            <div
              class="board-navigation-overlay__toolbar-item
              board-navigation-overlay__toolbar-item--fixed"
            >
              <NebulaButton
                :aria-label="$t('Exit full screen mode')"
                :disabled="!visible"
                type="flat"
                size="s"
                icon-right="arrow-left"
                @click="exitFullscreen"
              />
            </div>
            <div
              class="board-navigation-overlay__toolbar-item
              board-navigation-overlay__title-toolbar-item
              board-navigation-overlay__toolbar-item--divider-start"
            >
              <NebulaTitleTooltip
                class="board-navigation-overlay__tooltip"
                :class="{
                  'board-navigation-overlay__tooltip--disabled': !draftNameTooltipEnabled,
                }"
                position="block-end"
                :text="draftName"
              >
                <div
                  ref="draftName"
                  class="board-navigation-overlay__board-name"
                >
                  <span ref="draftNameText">{{ draftName }}</span>
                </div>
              </NebulaTitleTooltip>
            </div>
          </div>
          <div
            class="board-navigation-overlay__toolbar-group
              board-navigation-overlay__toolbar-group--middle"
          >
            <div class="board-navigation-overlay__toolbar-item">
              <BoardNavigationPager
                :disabled="!visible"
                :expanded="expanded"
                :expandable="expandable"
                :expands-down="true"
                background-theme="light"
                @expand-clicked="toggleExpanded"
              />
            </div>
          </div>
          <div
            class="board-navigation-overlay__toolbar-group
              board-navigation-overlay__toolbar-group--end"
          >
            <div
              v-if="showTools"
              class="
                board-navigation-overlay__toolbar-item
                board-navigation-overlay__tools-toolbar-item
              "
            >
              <NebulaDropdown
                ref="toolsMenu"
                button-id="fullscreen-header-tools"
                class="board-navigation-overlay__dropdown"
                use-white-background
              >
                <template
                  #trigger="{
                    toggleDropdown,
                    checkArrowKeyOnTrigger,
                    checkArrowKeyAfterTrigger,
                    isOpen,
                  }"
                >
                  <NebulaButton
                    id="fullscreen-header-tools"
                    :disabled="!visible"
                    type="flat"
                    icon-left="toolbox"
                    :icon-right="isOpen ? 'caret-up' : 'caret-down'"
                    haspopup="true"
                    size="s"
                    :text="$t('Tools')"
                    :aria-label="$t('Header tools menu')"
                    :aria-expanded="isOpen ? 'true' : 'false'"
                    @click="toggleDropdown"
                    @keydown="checkArrowKeyOnTrigger"
                    @keyup="checkArrowKeyAfterTrigger"
                  />
                </template>
                <template #content>
                  <NebulaDropdownItem
                    v-if="showTeacherNotes"
                    icon="notes"
                    :text="editorDrawers.notesFullscreen
                      ? $t('Hide Teacher Notes')
                      : $t('Show Teacher Notes')"
                    @dropdown-item-clicked="toggleNotesFullscreen"
                  />
                  <NebulaDropdownItem
                    v-if="showHighlightNotes"
                    icon="highlight-note"
                    :text="$t('My Notes')"
                    @dropdown-item-clicked="toggleHighlightNotes"
                  />
                  <NebulaDropdownItem
                    v-if="showImmersiveReader"
                    icon="immersive-reader"
                    :text="$t('Immersive Reader')"
                    @dropdown-item-clicked="openImmersiveReader"
                  />
                </template>
              </NebulaDropdown>
            </div>
            <div
              v-if="showSubmitAssignmentButton"
              class="
                board-navigation-overlay__toolbar-item
                board-navigation-overlay__submit-toolbar-item
              "
            >
              <NebulaButton
                size="s"
                :text="submitAssignmentButtonText"
                class="top-nav__submit-button"
                icon-right="arrow-right-circle"
                v-bind="submitAssignmentButton"
                @click="onSubmitAssignment(false)"
              />
            </div>
            <div
              v-if="showViewResults"
              class="
                board-navigation-overlay__toolbar-item
                board-navigation-overlay__view-results-toolbar-item
              "
            >
              <NebulaButton
                size="s"
                :link="viewResultsUrl"
                :text="$t('View Results')"
              />
            </div>
            <div
              v-if="showMarkAsComplete"
              class="
                board-navigation-overlay__toolbar-item
                board-navigation-overlay__mark-complete-toolbar-item
              "
            >
              <NebulaButton
                @click="markAssignmentAsComplete"
                size="s"
                :is-disabled="markingAssignmentComplete"
                :text="$t('Mark as Complete')"
              />
            </div>
            <div
              v-if="showCompleted"
              class="
                board-navigation-overlay__toolbar-item
                board-navigation-overlay__completed-toolbar-item
              "
            >
              <NebulaButton
                size="s"
                is-disabled
                :text="$t('Completed')"
              />
            </div>
            <div
              class="board-navigation-overlay__toolbar-item"
            >
              <NebulaDropdown
                ref="contextMenu"
                button-id="fullscreen-header-ellipsis"
                class="board-navigation-overlay__dropdown"
                use-white-background
              >
                <template
                  #trigger="{
                    toggleDropdown,
                    checkArrowKeyOnTrigger,
                    checkArrowKeyAfterTrigger,
                    isOpen,
                  }"
                >
                  <NebulaButton
                    id="fullscreen-header-ellipsis"
                    :disabled="!visible"
                    type="flat"
                    icon-right="ellipses-vertical"
                    haspopup="true"
                    size="s"
                    :aria-label="$t('Header context menu')"
                    :aria-expanded="isOpen ? 'true' : 'false'"
                    @click="toggleDropdown"
                    @keydown="checkArrowKeyOnTrigger"
                    @keyup="checkArrowKeyAfterTrigger"
                  />
                </template>
                <template #content>
                  <NebulaDropdownItem
                    icon="fullscreen-exit"
                    :text="$t('Exit Fullscreen')"
                    @dropdown-item-clicked="exitFullscreen"
                  />
                  <NebulaDropdownItem
                    v-if="showSubmitAssignmentButton"
                    class="board-navigation-overlay__submit-dropdown-item"
                    :icon="submitAssignmentButton?.iconRight
                      || submitAssignmentButton?.iconLeft
                      || 'arrow-right-circle'"
                    :text="$t('Submit Assignment')"
                    v-bind="submitAssignmentButton"
                    @dropdown-item-clicked="onSubmitAssignment(true)"
                  />
                  <NebulaDropdownItem
                    v-if="showViewResults"
                    class="board-navigation-overlay__view-results-dropdown-item"
                    icon="arrow-right-circle"
                    :text="$t('View Results')"
                    :link="viewResultsUrl"
                  />
                  <NebulaDropdownItem
                    v-if="showMarkAsComplete"
                    class="board-navigation-overlay__mark-complete-dropdown-item"
                    icon="arrow-right-circle"
                    :text="$t('Mark as Complete')"
                    @dropdown-item-clicked="markAssignmentAsComplete"
                  />
                  <NebulaDropdownItem
                    v-if="showCompleted"
                    heading
                    icon="arrow-right-circle"
                    class="board-navigation-overlay__completed-dropdown-item"
                    :text="$t('Completed')"
                  />
                  <template v-if="showTools">
                    <NebulaDropdownItem
                      heading
                      icon="toolbox"
                      class="board-navigation-overlay__tools-dropdown-item"
                      :text="$t('Tools')"
                    />
                    <NebulaDropdownItem
                      v-if="showTeacherNotes"
                      class="
                        nebula-dropdown__item--has-heading
                        board-navigation-overlay__tools-dropdown-item
                      "
                      icon="notes"
                      :text="editorDrawers.notesFullscreen
                        ? $t('Hide Teacher Notes')
                        : $t('Show Teacher Notes')"
                      @dropdown-item-clicked="toggleNotesFullscreen"
                    />
                    <NebulaDropdownItem
                      v-if="showHighlightNotes"
                      class="
                        nebula-dropdown__item--has-heading
                        board-navigation-overlay__tools-dropdown-item
                      "
                      icon="highlight-note"
                      :text="$t('My Notes')"
                      @dropdown-item-clicked="toggleHighlightNotes"
                    />
                    <NebulaDropdownItem
                      v-if="showImmersiveReader"
                      class="
                        nebula-dropdown__item--has-heading
                        board-navigation-overlay__tools-dropdown-item
                      "
                      icon="immersive-reader"
                      :text="$t('Immersive Reader')"
                      @dropdown-item-clicked="openImmersiveReader"
                    />
                  </template>
                </template>
              </NebulaDropdown>
            </div>
          </div>
        </template>
        <!-- Toolbar for default display mode -->
        <template v-else>
          <div class="board-navigation-overlay__toolbar-group">
            <div class="board-navigation-overlay__toolbar-item">
              <BoardNavigationPager
                :expanded="expanded"
                :expandable="expandable"
                @expand-clicked="toggleExpanded"
              />
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="expandable"
        class="board-navigation-overlay__pages"
      >
        <div
          aria-live="polite"
          class="nebula-screenreader-only"
        >
          <div v-if="expanded">
            {{ $t('Page drawer open') }}
          </div>
          <div v-else>
            {{ $t('Page drawer closed') }}
          </div>
        </div>
        <button
          v-if="showScrollPagesLeft"
          :aria-label="draftType === 'board'
            ? $t('Scroll to previous pages')
            : $t('Scroll to previous slides')"
          class="board-navigation-overlay__scroll-button
            board-navigation-overlay__scroll-button--previous nebula-button nebula-button--s
            nebula-button--pill nebula-button--fill nebula-button--icon-left
            nebula-button--icon-only"
          :disabled="!expanded || !visible"
          @click="scrollPages('left')"
        >
          <NebulaIcon
            symbol-id="arrow-left"
            class-override="nebula-button__icon"
          />
        </button>
        <div
          ref="pagesScrollContainer"
          class="board-navigation-overlay__pages-scroll-container"
          @scroll="onPagesScroll"
          @keyup="onPagesScrollKeyup"
          @keydown="onPagesScrollKeydown"
        >
          <div
            v-for="pageOrSection in groupedPages"
            :key="pageOrSection.id || pageOrSection.sectionId"
          >
            <template v-if="pageOrSection.isSection">
              <div class="board-navigation-overlay__section-header">
                <NebulaIcon
                  class="board-navigation-overlay__section-icon"
                  size="s"
                  :symbol-id="getSectionById(pageOrSection.sectionId).icon || 'information'"
                />
                {{ getSectionById(pageOrSection.sectionId).name || $t('Section') }}
              </div>
              <ul class="board-navigation-overlay__section-pages">
                <li
                  v-for="(page, index) in pageOrSection.pages"
                  :key="page.sectionId"
                >
                  <BoardNavigationOverlayPage
                    ref="pageButtons"
                    :expanded-and-visible="expanded && visible"
                    :page="page"
                    :section-name="index === 0
                      ? getSectionById(pageOrSection.sectionId).name
                      : null"
                  />
                </li>
              </ul>
            </template>
            <BoardNavigationOverlayPage
              v-else
              ref="pageButtons"
              :key="pageOrSection.id"
              :expanded-and-visible="expanded && visible"
              :page="pageOrSection"
            />
          </div>
        </div>
        <button
          v-if="showScrollPagesRight"
          :aria-label="draftType === 'board'
            ? $t('Scroll to next pages')
            : $t('Scroll to next slides')"
          class="board-navigation-overlay__scroll-button
            board-navigation-overlay__scroll-button--next nebula-button nebula-button--s
            nebula-button--pill nebula-button--fill nebula-button--icon-left
            nebula-button--icon-only"
          :disabled="!expanded || !visible"
          @click="scrollPages('right')"
        >
          <NebulaIcon
            symbol-id="arrow-right"
            class-override="nebula-button__icon"
          />
        </button>
      </div>
      <div
        v-if="editorIsFullscreen && isMobileOrTablet()
          && !notesVisible && !highlightNotesVisible"
        class="board-navigation-overlay__fullscreen-expand"
      >
        <button
          class="board-navigation-overlay__fullscreen-expand-button
            nebula-button nebula-button--s nebula-button--pill
            nebula-button--icon-left nebula-button--icon-only"
          :aria-label="visible ? $t('Close toolbar') : $t('Open toolbar')"
          @click="visible = !visible"
        >
          <NebulaIcon
            :symbol-id="visible ? 'caret-down' : 'caret-up'"
            class-override="nebula-button__icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { get, throttle } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { ResizeSensor } from 'css-element-queries';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import {
  NebulaDropdown,
  NebulaDropdownItem,
  NebulaIcon,
  NebulaButton,
  NebulaTitleTooltip,
} from '@discoveryedu/nebula-components';
import BoardNavigationOverlayPage from '@/components/navigation/BoardNavigationOverlayPage.vue';
import BoardNavigationPager from '@/components/navigation/BoardNavigationPager.vue';
import { isMobileOrTablet } from '@/lib/utils';
import assignmentMixin from '@/mixins/assignment';
import { bus } from '@/lib/eventBus';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'BoardNavigationOverlay',
  components: {
    NebulaIcon,
    NebulaButton,
    NebulaDropdown,
    NebulaDropdownItem,
    NebulaTitleTooltip,
    BoardNavigationOverlayPage,
    BoardNavigationPager,
  },
  mixins: [assignmentMixin],
  props: {
    enableSlideDrawer: {
      type: Boolean,
      default: true,
    },
    showImmersiveReader: {
      type: Boolean,
      default: true,
    },
    showNotes: {
      type: Boolean,
      default: true,
    },
    showHighlightNotes: {
      type: Boolean,
      default: false,
    },
    hideSubmitButton: {
      type: Boolean,
      default: false,
    },
    slideOverlayArrows: {
      type: Boolean,
      default: false,
    },
    themeOverride: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: 'VIEW',
    },
    submitAssignmentButton: {
      type: Object,
      default: null,
    },
  },
  emits: ['immersive-reader-clicked'],
  data() {
    return {
      expanded: false,
      expandingOrContracting: false,
      canScrollPagesLeft: false,
      canScrollPagesRight: false,
      pagesScrollLeft: 0,
      nebulaSpace1x: parseInt(nebulaTokens.space['1x'], 10),
      nebulaBreakpointDesktop: parseInt(nebulaTokens.breakpoints.desktop, 10),
      scrollContainerResizeSensor: null,
      visible: true,
      noActivityTimeout: null,
      isMobileOrTablet,
      windowWidth: Infinity,
      draftNameTooltipEnabled: false,
    };
  },
  created() {
    this.updateScrollButtons = throttle(() => {
      const scrollContainer = this.$refs?.pagesScrollContainer;
      if (!scrollContainer) return;
      const { scrollLeft } = scrollContainer;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;

      this.canScrollPagesLeft = scrollLeft > 0;
      this.canScrollPagesRight = scrollLeft < maxScrollLeft;
    }, 100);

    this.onMouseMove = throttle((event) => {
      // If we mouse over the top 25% of the viewport, show the navigation overlay
      if (event.clientY <= window.innerHeight * 0.25) {
        this.visible = true;
      }
    }, 10);

    this.onTabKeydown = (event) => {
      if (event.keyCode === 9) {
        this.visible = true;
      }
    };

    if (this.activityTimeoutEnabled) {
      this.onActivityTimeoutEnabled();
    }
  },
  mounted() {
    this.attachResizeSensor();
    bus.on('window:resize', this.onWindowResize);
    this.onWindowResize();
  },
  updated() {
    if (this.$refs?.pagesScrollContainer) {
      this.$refs.pagesScrollContainer.scrollLeft = this.pagesScrollLeft;
    }
  },
  beforeUnmount() {
    if (this.scrollContainerResizeSensor) {
      ResizeSensor.detach(this.$refs.pagesScrollContainer);
      this.scrollContainerResizeSensor = null;
    }
    this.onActivityTimeoutDisabled();
    bus.off('window:resize', this.onWindowResize);
  },
  watch: {
    draft() {
      this.$nextTick(() => {
        this.updateScrollButtons();
        this.attachResizeSensor();
      });
    },
    expanded() {
      if (this.expandingCollapsingTimeout) {
        clearTimeout(this.expandingCollapsingTimeout);
      }
      this.expandingOrContracting = true;
      this.expandingCollapsingTimeout = setTimeout(() => {
        this.expandingOrContracting = false;
      }, 250); // duration of slide drawer transition
    },
    currentPageIndex() {
      if (this.expanded) this.scrollToSelectedPage();

      if (this.editorIsFullscreen && (this.notesVisible || this.highlightNotesVisible)) {
        this.visible = true;
      }
    },
    activityTimeoutEnabled() {
      if (this.activityTimeoutEnabled) {
        this.onActivityTimeoutEnabled();
      } else {
        this.onActivityTimeoutDisabled();
      }
    },
    visible() {
      this.closeDropdowns();
    },
    highlightNotesVisible() {
      if (this.highlightNotesVisible) {
        this.visible = true;
      }
    },
    homeworkCompleted(completed) {
      if (completed) {
        // After assignment is submitted, exit full screen mode.
        this.exitFullscreen();
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'draft',
      'editorDrawers',
      'editorIsFullscreen',
      'currentPageIndex',
      'currentPage',
      'groupedPages',
      'draftName',
      'draftType',
      'pagesSorted',
      'studentView',
    ]),
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    draftHasSections() {
      // Read from the pages array instead of checking draft.options.groups,
      // in case there is any unused group data
      return this.pagesSorted.some((page) => page.options && page.options.group_id);
    },
    expandable() {
      return this.enableSlideDrawer && this.pagesSorted?.length > 1;
    },
    showTools() {
      return this.showTeacherNotes || this.showHighlightNotes || this.showImmersiveReader;
    },
    showScrollPagesLeft() {
      return !isMobileOrTablet()
        && !this.expandingOrContracting
        && this.expanded
        && this.canScrollPagesLeft;
    },
    showScrollPagesRight() {
      return !isMobileOrTablet()
        && !this.expandingOrContracting
        && this.expanded
        && this.canScrollPagesRight;
    },
    activityTimeoutEnabled() {
      return this.editorIsFullscreen
        && !this.notesVisible
        && !this.highlightNotesVisible
        && !isMobileOrTablet();
    },
    notesVisible() {
      return this.showNotes && this.editorDrawers.notesFullscreen && this.currentPageHasNotes;
    },
    highlightNotesVisible() {
      return this.editorDrawers.highlightNotes;
    },
    showTeacherNotes() {
      return this.showNotes
        && this.draft.pages.some((page) => page.notes?.length)
        && !this.userIsStudent && !this.studentView;
    },
    currentPageHasNotes() {
      return this.currentPage?.notes?.length;
    },
    submitAssignmentButtonText() {
      return this.windowWidth >= this.nebulaBreakpointDesktop
        ? this.$t('Submit Assignment')
        : this.$t('Submit');
    },
    showSubmitAssignmentButton() {
      if (this.submitAssignmentButton) {
        return this.submitAssignmentButton?.isVisible;
      }
      return this.showSubmitAssignment;
    },
    homeworkCompleted() {
      return !!this.submitAssignmentButton?.homework?.completed;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
      types.TOGGLE_EDITOR_DRAWER,
    ]),
    closeDropdowns() {
      const dropdowns = [
        this.$refs?.contextMenu,
        this.$refs?.toolsMenu,
      ];
      dropdowns.forEach((dropdown) => {
        if (dropdown) {
          dropdown.closeDropdown();
        }
      });
    },
    getSectionById(id) {
      return get(this.draft, 'options.groups', [])
        .find((section) => section.id === id)
        || {};
    },
    onPagesScroll() {
      this.pagesScrollLeft = this.$refs.pagesScrollContainer.scrollLeft;
      this.updateScrollButtons();
    },
    onPagesScrollKeydown(event) {
      if (['Left', 'ArrowLeft', 'Right', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
      }
    },
    onPagesScrollKeyup(event) {
      if (['Left', 'ArrowLeft'].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        this[types.VIEW_PREV_PAGE]();
      } else if (['Right', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        this[types.VIEW_NEXT_PAGE]();
      }
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      if (this.expanded) this.scrollToSelectedPage(false);
    },
    scrollPages(direction) {
      const scrollContainer = this.$refs.pagesScrollContainer;
      const { scrollLeft } = scrollContainer;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const scrollRect = this.$refs.pagesScrollContainer.getBoundingClientRect();
      let distance = scrollContainer.clientWidth;
      const { pageButtons } = this.$refs;

      /*
        Find the first page in the direction we're scrolling that isn't completely
        visible in the slide drawer boundaries, and then scroll to the start of that
        page so it becomes completely visible.
      */
      if (direction === 'right') {
        for (let pageIndex = 0; pageIndex < this.pagesSorted.length; pageIndex += 1) {
          const pageRect = pageButtons[pageIndex].$el.getBoundingClientRect();
          if (pageRect.right > scrollRect.right) {
            distance = pageRect.left - this.nebulaSpace1x - scrollRect.left;
            break;
          }
        }
      } else {
        for (let pageIndex = this.pagesSorted.length - 1; pageIndex >= 0; pageIndex -= 1) {
          const pageRect = pageButtons[pageIndex].$el.getBoundingClientRect();
          if (pageRect.left < scrollRect.left) {
            distance = scrollRect.right - pageRect.right - this.nebulaSpace1x;
            break;
          }
        }
      }

      const newScrollLeft = direction === 'left'
        ? Math.max(scrollLeft - distance, 0)
        : Math.min(scrollLeft + distance, maxScrollLeft);

      if (scrollContainer.scrollTo) {
        scrollContainer.scrollTo({
          behavior: 'smooth',
          left: newScrollLeft,
        });
      } else {
        scrollContainer.scrollLeft = newScrollLeft;
      }
    },
    scrollToSelectedPage(animated = true) {
      this.$nextTick(() => {
        const pageEl = this.$refs.pageButtons[this.currentPageIndex].$el;
        const pageRect = pageEl.getBoundingClientRect();
        const scrollContainer = this.$refs.pagesScrollContainer;
        const scrollRect = scrollContainer.getBoundingClientRect();
        const { scrollLeft } = scrollContainer;

        let newScrollLeft = scrollLeft;
        if (pageRect.left < scrollRect.left) {
          newScrollLeft -= scrollRect.left - pageRect.left + this.nebulaSpace1x;
        } else if (pageRect.right > scrollRect.right) {
          newScrollLeft += pageRect.right - scrollRect.right + this.nebulaSpace1x;
        }

        if (scrollContainer.scrollTo && animated) {
          scrollContainer.scrollTo({
            behavior: 'smooth',
            left: newScrollLeft,
          });
        } else {
          scrollContainer.scrollLeft = newScrollLeft;
        }
      });
    },
    attachResizeSensor() {
      this.$nextTick(() => {
        if (!this.scrollContainerResizeSensor && this.$refs.pagesScrollContainer) {
          this.scrollContainerResizeSensor = new ResizeSensor(
            this.$refs.pagesScrollContainer,
            this.updateScrollButtons,
          );
        }
      });
    },
    exitFullscreen() {
      bus.emit('exitFullscreen');
    },
    onActivityTimeoutEnabled() {
      this.startNoActivityTimeout(5000);
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('keydown', this.onTabKeydown);
    },
    onActivityTimeoutDisabled() {
      this.clearNoActivityTimeout();
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('keydown', this.onTabKeydown);
    },
    clearNoActivityTimeout() {
      if (this.noActivityTimeout) {
        clearTimeout(this.noActivityTimeout);
        this.noActivityTimeout = null;
      }
    },
    startNoActivityTimeout(timeout = 3000) {
      this.clearNoActivityTimeout();
      this.noActivityTimeout = setTimeout(() => {
        this.visible = false;
      }, timeout);
    },
    onMouseOver() {
      this.clearNoActivityTimeout();
      this.visible = true;
    },
    onMouseLeave() {
      this.startNoActivityTimeout();
    },
    onKeyDown() {
      if (this.noActivityTimeout) {
        /*
          If a key is pressed while focused in the header but mouse is not
          still within the header boundaries, then extend the timeout to
          keep the header visible while pressing keys.
        */
        this.startNoActivityTimeout(5000);
      }
    },
    toggleNotesFullscreen() {
      this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'notesFullscreen' });
      this.closeDropdowns();
    },
    toggleHighlightNotes() {
      this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'highlightNotes' });
      this.closeDropdowns();
    },
    openImmersiveReader() {
      this.$emit('immersive-reader-clicked');
      this.closeDropdowns();
    },
    onSubmitAssignment(exitFullscreen = false) {
      this.closeDropdowns();
      if (this.submitAssignmentButton) {
        const action = this.submitAssignmentButton?.action;
        if (action) {
          action();
          if (exitFullscreen) {
            this.exitFullscreen();
          }
        }
      } else {
        this.openSubmitAssignmentModal();
      }
    },
    onWindowResize() {
      this.windowWidth = window.innerWidth;
      // Determine if draft name tooltip is needed.
      const { draftName, draftNameText } = this.$refs;
      if (draftName && draftNameText) {
        /*
          Width of text content must be greater than the container by 2 pixels
          in order for the tooltip to be enabled.
        */
        const textWidth = draftNameText.getBoundingClientRect().width - 2;
        this.draftNameTooltipEnabled = textWidth > draftName.clientWidth;
      }
    },
  },
};
</script>

<style lang="stylus">
.board-navigation-overlay {
  --pages-scroll-container-height: 103px;

  nebula-text-body-1();
  background-color: rgba(40, 57, 62, 0.7); // special color per design
  border-radius: 0 0 $nebula-border-radius-button-default $nebula-border-radius-button-default;
  color: $nebula-color-white;
  left: 0;
  position: absolute;
  right: 0;
  transition: $nebula-transition-default;
  z-index: 212; // Above editor drawers

  &--no-transition {
    transition: none;
  }

  &--default {
    bottom: 0;
  }

  &--hidden {
    transform: translateY(100%);

    &.board-navigation-overlay--expanded {
      transform: translateY(0);
    }
  }

  &--fullscreen {
    --pages-scroll-container-height: 111px;

    border-radius: 0;
    position: fixed;
    top: 0;

    &.board-navigation-overlay--hidden {
      transform: translateY(-100%);

      &.board-navigation-overlay--expanded {
        transform: translateY(0);
      }
    }

    .board-navigation-overlay {
      &__page-button {
        margin-top: $nebula-space-1x;
      }

      &__toolbar {
        background: $nebula-color-white;
        box-shadow: 0px 4px 12px rgba(189, 189, 189, 0.25); // custom per design
        display: flex;
        height: $nebula-space-7x;
        justify-content: space-between;
      }

      &__toolbar-item {
        height: $nebula-space-7x;
      }

      &__dropdown {
        z-index: 5;
      }
    }
  }

  &--no-sections {
    --pages-scroll-container-height: 73px;

    &.board-navigation-overlay--fullscreen {
      --pages-scroll-container-height: 81px;
    }
  }

  &--expanded {
    .board-navigation-overlay__pages {
      height: var(--pages-scroll-container-height);
    }
  }

  &__toolbar {
    align-items: center;
    display: flex;
    height: $nebula-space-6x;
    justify-content: center;
  }

  &__toolbar-group {
    display: flex;
    justify-content: center;

    &--pager {
      flex-grow: 1;
    }

    &--start {
      flex: 1 1;
      justify-content: flex-start;

      .board-navigation-overlay__toolbar-item {
        &:not(:first-child) {
          padding-inline-start: 0;
        }
      }
    }

    &--middle {
      flex: 0 0;

      .board-navigation-overlay__toolbar-item {
        &:first-child {
          padding-inline-start: 0;
        }

        &:last-child {
          padding-inline-end: 0;
        }
      }
    }

    &--end {
      flex: 1 1;
      justify-content: flex-end;

      .board-navigation-overlay__toolbar-item {
        &:not(:last-child) {
          padding-inline-end: 0;
        }
      }
    }
  }

  &__toolbar-item {
    align-items: center;
    display: flex;
    justify-content: center;
    height: $nebula-space-6x;
    padding: 0 $nebula-space-1x;

    &--divider-start& {
      &:not(:first-child) {
        border-inline-start: 1px solid #c4c4c4; // special color per design
        padding-inline-start: $nebula-space-1x;
      }
    }

    &--fixed {
      flex: 0 0;
    }
  }

  &__title-toolbar-item {
    flex: 1 1;
    justify-content: flex-start;
    position: relative;
  }

  &__dropdown {
    --nebula-dropdown-heading-text-transform: none;
    --nebula-dropdown-heading-weight: normal;
    --nebula-dropdown-item-icon-fill: var(----nebula-dropdown-item-text-color);

    .nebula-dropdown__item {
      --nebula-dropdown-item-icon-size: $nebula-space-2x;

      &--has-heading {
        padding-inline-start: $nebula-space-3x;
      }
    }
  }

  &__board-name {
    color: $nebula-color-interface-blue-600;
    display: inline-block;
    font-size: $nebula-font-size-body-2;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__pages {
    height: 0px;
    position: relative;
    transition: $nebula-transition-default;
    width: 100%;
  }

  &__pages-scroll-container {
    align-items: flex-end;
    bottom: 0;
    display: flex;
    gap: $nebula-space-1x;
    left: 0;
    list-style-type: none;
    margin: 0;
    overflow-x: scroll;
    overflow-y: hidden;
    padding: 0 $nebula-space-1x;
    position: absolute;
    right: 0;
    scrollbar-width: none;  // Firefox
    top: 0;
    -ms-overflow-style: none;  // Edge
  }

  &__pages-scroll-container::-webkit-scrollbar {
    display: none;
  }

  &__section-header {
    align-items: center;
    border-bottom: 2px dotted $nebula-color-white;
    display: flex;
    font-size: $nebula-font-size-caption;
    gap: $nebula-space-1x;
    justify-content: flex-start;
    margin-bottom: $nebula-space-half;
    padding-bottom: $nebula-space-half;
  }

  &__section-icon {
    fill: $nebula-color-white;
  }

  &__section-pages {
    display: flex;
    gap: $nebula-space-1x;
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  &__scroll-button {
    background-color: $nebula-color-neutral-500;
    position: absolute;
    top: calc(50% - 14px);
    z-index: 10;

    &:hover,
    &:focus {
      background: $nebula-color-neutral-600;
    }

    &:active {
      background: $nebula-color-neutral-600;
    }

    &--next {
      right: $nebula-space-1x;
    }

    &--previous {
      left: $nebula-space-1x;
    }
  }

  &__fullscreen-expand {
    height: 0;
    position: relative;
    width: 100%;
  }

  &__fullscreen-expand-button {
    $fullscreen-expand-button-color = rgba(40, 57, 62, 0.2); // special color per design
    background: $fullscreen-expand-button-color;
    border: 1px solid $nebula-color-white;
    border-radius: 50%;
    height: $nebula-space-6x;
    justify-content: center;
    left: "calc(50% - %s)" % $nebula-space-3x;
    position: absolute;
    top: $nebula-space-1x;
    width: $nebula-space-6x;
    &:hover, &:active {
      background: $fullscreen-expand-button-color;
    }
    svg {
      fill: $nebula-color-white;
    }
  }

  &--hide-nav-overlay {
    display: none;
  }

  &__tooltip {
    max-width: "calc(100% - %s)" % $nebula-space-2x;
    position: absolute;

    .nebula-title-tooltip {
      display: inline-block;
      z-index: 5;

      &__trigger {
        align-items: center;
        display: flex;
      }

      &__text {
        max-width: 350px;
      }
    }

    &--disabled {
      .nebula-title-tooltip {
        display: none;
      }
    }
  }

  @media (min-width: $nebula-breakpoints-desktop) {
    // Hide tools options in context menu for larger screen sizes.
    &__tools-dropdown-item {
      display: none;
    }
  }

  @media (max-width: ($nebula-breakpoints-desktop - 1px)) {
    // Hide tools dropdown menu for smaller screen sizes
    &__tools-toolbar-item {
      display: none;
    }

    // For smaller screens, don't allow end group to expand.
    &__toolbar-group--end {
      flex: 0 0;
    }
  }

  @media (max-width: ($nebula-breakpoints-tablet-portrait - 1px)) {
    // Hide submit buttons in toolbar for smaller screens sizes
    &__submit-toolbar-item,
    &__view-results-toolbar-item,
    &__mark-complete-toolbar-item,
    &__completed-toolbar-item {
      display: none;
    }
  }

  @media (min-width: $nebula-breakpoints-tablet-portrait) {
    // Hide submit dropdown options for larger screen sizes.
    &__submit-dropdown-item,
    &__view-results-dropdown-item,
    &__mark-complete-dropdown-item,
    &__completed-dropdown-item {
      display: none;
    }
  }

  @media (max-width: ($nebula-breakpoints-mobile-landscape - 1px)) {
    &__tooltip {
      .nebula-title-tooltip {
        &__text {
          max-width: 200px;
        }
      }
    }
  }
}
</style>
